import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { selectUserCognitoGroups } from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";
import {
  InstitutionFormErrors,
  InstitutionNetworkErrors,
  useInstitutionById,
  useUpdateInstitution
} from "./institutionUtils";
import { SubmitHandler, useForm } from "react-hook-form";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { Alert, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { UpdateInstitutionInput } from "../../API";

const pageDetails: PageWrapperProps = {
  className: "EditInstitution",
  headerText: "Edit Institution"
};

export type EditInstitutionInputs = {
  institutionId: string | null;
  name: string | null;
  description?: string | null;
};

export const EditInstitution = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUsersPermissions = useAppSelector(selectUserCognitoGroups);
  const isSysAdmin = currentUsersPermissions?.includes(
    CognitoUserGroups.SysAdmins
  );

  const {
    institution,
    status: useInstitutionStatus,
    error: useInstitutionError
  } = useInstitutionById(id);
  const {
    updateInstitution,
    status: updateInstitutionStatus,
    error: updateInstitutionError,
    updatedInstitution
  } = useUpdateInstitution();
  const anyApiIsLoading = [
    useInstitutionStatus,
    updateInstitutionStatus
  ].includes("loading");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<EditInstitutionInputs>({
    reValidateMode: "onChange"
  });

  // A flag to say whether we have the data loaded and set in the form.
  const [formPopulated, setFormPopulated] = useState(false);

  const onSubmit: SubmitHandler<EditInstitutionInputs> = async (data) => {
    const { institutionId, name, description } = data;
    if (institutionId) {
      const updateInstitutionInput: UpdateInstitutionInput = {
        id: institutionId,
        name: name,
        description: description
      };
      const result = await updateInstitution(updateInstitutionInput);
      if (result !== null) {
        navigate(`/institutions/${id}`);
      }
    }
  };

  // When the institution from the backend is updated, we need to populate the form
  useEffect(() => {
    if (institution) {
      const { name, description, id } = institution;
      reset({
        name: name,
        description: description,
        institutionId: id
      });
      setFormPopulated(true);
    }
  }, [institution]);

  // Display an appropriate messge if the logged-in user isn't a sysadmin
  if (!isSysAdmin) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Only SysAdmins can edit an Institution.")}</h2>
      </PageWrapper>
    );
  }

  // No requests have yet been made to feth the data.
  if (useInstitutionStatus === "idle") {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Awaiting data...")}</h2>
      </PageWrapper>
    );
  }

  // Loading of necessary data is in progress.
  if (useInstitutionStatus === "loading") {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  // One of the necessary pieces of data experienced an error loading.
  if (useInstitutionStatus === "failed") {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error fetching institution!")}</h2>
        <p>{JSON.stringify(useInstitutionError)}</p>
      </PageWrapper>
    );
  }

  // Loaded the data, but the institution doesn't exist.
  if (useInstitutionStatus === "succeeded" && !institution) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Institution not found!")}</h2>
      </PageWrapper>
    );
  }

  // Loaded the data, have a valid institution, but form hasn't been populated yet. Don't display, the form might display improperly
  if (useInstitutionStatus === "succeeded" && institution && !formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Populating form...")}</h2>
      </PageWrapper>
    );
  }

  // Loaded the data, have a valid institution, and the form has been populated
  // with the necessary data. Show the form.
  if (useInstitutionStatus === "succeeded" && institution && formPopulated) {
    return (
      <PageWrapper {...pageDetails}>
        <form
          className="NewInstitutionForm"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Grid container spacing={2} direction="column">
            {updateInstitutionError && (
              <Grid xs={8}>
                <Alert severity="error">
                  {t(InstitutionNetworkErrors.UpdateInstitutionError)}
                </Alert>
              </Grid>
            )}
            <Grid xs={8}>
              <label htmlFor="name">{t("Institution Name")}</label>
              <input
                type="text"
                required
                placeholder={t("Institution Name")}
                style={{ width: "100%" }}
                id="name"
                disabled={anyApiIsLoading}
                {...register("name", { required: true })}
              />
              {errors.name && (
                <Alert severity="error">
                  {t(InstitutionFormErrors.EmptyInstitutionName)}
                </Alert>
              )}
            </Grid>
            <Grid xs={8}>
              <label htmlFor="description">
                {t("Institution Description")}
              </label>
              <textarea
                placeholder={t("Institution Description")}
                style={{ width: "100%" }}
                id="description"
                disabled={anyApiIsLoading}
                {...register("description")}
              />
            </Grid>
          </Grid>
          <button disabled={anyApiIsLoading}>{t("Save")}</button>
          <button
            disabled={anyApiIsLoading}
            onClick={() => navigate(`/institutions/${id}`)}
          >
            {t("Cancel")}
          </button>
        </form>
      </PageWrapper>
    );
  }

  // Null return to satisfy typescript requirements
  return null;
};
